/**
 * Wire contract for the pull path (spec §4 "Pull endpoints", §6).
 *
 * These schemas mirror the server's pydantic DTOs field-for-field, including
 * snake_case. That is deliberate: a camelCase rename layer would be one more
 * place for the two sides to drift silently, and the pull code translates into
 * local SQLite column names anyway — which are snake_case too.
 *
 * The domain schemas in ./session and ./capture stay camelCase because they
 * model the app's own state, not the wire.
 */
import { z } from 'zod';
import { FormTemplate } from './form';

/** Server row fields present on every pullable resource. */
const ServerRowBase = {
  id: z.string(),
  org_id: z.string(),
  version: z.number().int(),
  /** Non-null means the row was soft-deleted; the phone deletes locally. */
  deleted_at: z.string().nullable(),
  updated_at: z.string(),
};

export const ServerSessionRow = z.object({
  ...ServerRowBase,
  title: z.string(),
  template_id: z.string().nullable(),
  // Defaulted for the same reason as `start_location` below: a server that
  // predates the column omits the key, and a required field would drop the
  // whole row rather than the one value.
  project_id: z.string().nullable().default(null),
  status: z.string(),
  owner_id: z.string(),
  reviewer_id: z.string().nullable(),
  meta: z.record(z.unknown()).default({}),
  // Defaulted, not just nullable: a server that predates the column omits the
  // key entirely, and a required field would fail the whole row rather than
  // the one value — which is how a session disappears off the phone.
  start_location: z.record(z.unknown()).nullable().default(null),
  // Defaulted for the same reason as `start_location`: a server that predates
  // the column omits the key, and a required field would drop the whole row
  // rather than the one value.
  review_note: z.string().nullable().default(null),
  flagged_fields: z.array(z.string()).default([]),
  submitted_at: z.string().nullable(),
  approved_at: z.string().nullable(),
  created_at: z.string(),
});
export type ServerSessionRow = z.infer<typeof ServerSessionRow>;

export const ServerCaptureRow = z.object({
  ...ServerRowBase,
  session_id: z.string(),
  kind: z.string(),
  media_id: z.string().nullable(),
  byte_size: z.number().int(),
  gps: z.record(z.unknown()).nullable(),
  tags: z.array(z.unknown()).default([]),
  ai_fields: z.record(z.unknown()).default({}),
  // Optional for a client talking to a server that predates migration 0028.
  ai_status: z.string().default(''),
  ai_detail: z.string().default(''),
  linked_field_id: z.string().nullable(),
  payload: z.record(z.unknown()).default({}),
  // Optional so a client can talk to a server that predates migration 0026;
  // nullable because the row itself may carry no device time.
  captured_at: z.string().nullable().default(null),
  created_at: z.string(),
});
export type ServerCaptureRow = z.infer<typeof ServerCaptureRow>;

/**
 * A field value has no `id` of its own — the primary key is composite, so
 * `ServerRowBase` does not apply and the shared fields are spelled out.
 */
export const ServerFieldValueRow = z.object({
  org_id: z.string(),
  version: z.number().int(),
  deleted_at: z.string().nullable(),
  updated_at: z.string(),
  session_id: z.string(),
  field_id: z.string(),
  value: z.unknown(),
  source: z.string(),
  confidence: z.number().nullable(),
  source_capture_id: z.string().nullable(),
});
export type ServerFieldValueRow = z.infer<typeof ServerFieldValueRow>;

/**
 * A project as the phone holds it.
 *
 * This is `GET /projects/mine`, not the console's project list: the counts are
 * the signed-in user's own, and the default template is expanded to a name
 * because the phone has no template list to join against while offline.
 *
 * It has no `deleted_at`/`version` pair and so does not extend `ServerRowBase`
 * — the endpoint returns the current assignment set rather than a change feed,
 * and a project leaving that set is an unassignment, not a soft delete. The
 * phone replaces its table wholesale on each pull.
 */
export const ServerProjectRow = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  code: z.string(),
  status: z.string(),
  default_template_id: z.string().nullable().default(null),
  default_template_name: z.string().nullable().default(null),
  my_session_count: z.number().int().nonnegative().default(0),
  last_worked_at: z.string().nullable().default(null),
});
export type ServerProjectRow = z.infer<typeof ServerProjectRow>;

/**
 * A template's `definition` column: everything `FormTemplate` describes except
 * `id` and `name`, which are columns on the server rather than document fields.
 * Resolves the mismatch where the server stored an ad-hoc `{fields: [...]}`
 * shape that `FormTemplate.parse()` could not accept.
 */
export const TemplateDefinition = FormTemplate.omit({ id: true, name: true });
export type TemplateDefinition = z.infer<typeof TemplateDefinition>;

export const ServerTemplateRow = z.object({
  ...ServerRowBase,
  name: z.string(),
  definition: TemplateDefinition,
});
export type ServerTemplateRow = z.infer<typeof ServerTemplateRow>;

/**
 * Page envelope shared by every pull endpoint.
 *
 * `server_time` is the authoritative watermark for the next pull — the phone
 * never seeds a watermark from its own clock (spec §6).
 */
export function PullPage<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    server_time: z.string(),
    next_cursor: z.string().nullable().optional(),
  });
}

export type PullPage<T> = {
  items: T[];
  server_time: string;
  next_cursor?: string | null;
};

/** Response envelope for the single-row pulls: GET /sessions/{id}, /captures/{id}. */
export function OnePull<T extends z.ZodTypeAny>(row: T) {
  return z.object({ row, version: z.number().int() });
}

/** The three resources `pullAll()` walks, and their watermark keys (spec §6). */
export const PullResource = z.enum([
  'templates',
  // The projects assigned to this user. Pulled with the templates rather than
  // with the sessions: it is reference data the capture flow reads before
  // there is anything to sync, and an inspector who opens the app offline
  // still needs to know which projects are theirs.
  'projects_mine',
  'sessions_mine',
  'sessions_reviewed',
  // Submissions waiting on this reviewer. Distinct from `sessions_mine`,
  // which is what this user *owns* — a reviewer's queue is other people's
  // work, and without it the phone's inbox could only ever list submissions
  // made on this device.
  'sessions_review_queue',
  'captures_mine',
  'field_values_mine',
]);
export type PullResource = z.infer<typeof PullResource>;

/** Watermark row in the client's `sync_watermarks` table. */
export const SyncWatermark = z.object({
  resource: PullResource,
  cursor: z.string(),
  updatedAt: z.string(),
});
export type SyncWatermark = z.infer<typeof SyncWatermark>;

/**
 * Error envelope from the API (spec §8). `details.server_version` is what the
 * conflict UI needs to resolve a 409 without a second round trip.
 */
export const ApiErrorBody = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.unknown()).nullish(),
  trace_id: z.string().nullish(),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBody>;
